import { MessageBar, MessageBarType } from "@fluentui/react";
import React from "react";
import { LanguageContext } from "../../utilities/LocalizationModule";

export interface IUnfilledPlaceholdersMessageBarProps {
  placeholders: string[];
  /** Values the server reported as matching no live token (stale envelope, renamed field). */
  unusedValues?: string[];
  /** ALL leftover placeholder-shaped tokens the server reported (its one bucket mixes
   *  destination-column tokens and unresolved CRM tokens); split here by prefix. */
  columnPlaceholders?: string[];
  /** Non-null per-family resolution failure reasons (fortes/simplicate/exact) from the
   *  creation response — server-generated English sentences, shown verbatim. */
  crmResolutionReasons?: string[];
  onDismiss: () => void;
}

// Case-insensitive mirrors of the server token grammars (FortesTemplatePlaceholders /
// SimplicateTemplatePlaceholders / ExactTemplatePlaceholders, all OrdinalIgnoreCase).
const CRM_TOKEN_PREFIXES = ["<<[fortes:", "<<[simplicate:", "<<[exact:"];
const isCrmToken = (token: string): boolean => {
  const lower = token.toLowerCase();
  return CRM_TOKEN_PREFIXES.some(prefix => lower.startsWith(prefix));
};

/**
 * Post-creation warning for create-from-template. The save panel closes itself on
 * success, so this bar lives in the content body where it survives the panel: it lists
 * every placeholder the server reported as unfilled and every provided value that
 * matched no token (warn-never-block) as bullets, and stays until dismissed, replaced
 * by the next creation, or the user changes directory. Both lists render here - a toast
 * fired just before the panel closes dies with the panel's ToastContainer.
 *
 * The server's leftover-token bucket deliberately mixes families (SharePointHelper's
 * post-fill sweep reports every surviving token); rendering it under one "no matching
 * column" heading mislabelled CRM tokens, whose failure has nothing to do with columns.
 * The split lives HERE, not in callers, so every consumer gets the honest grouping.
 *
 * Memoized: it sits inside the grid component, which re-renders on every interaction -
 * with stable props this stays out of that hot path entirely.
 */
export const UnfilledPlaceholdersMessageBar = React.memo(({ placeholders, unusedValues, columnPlaceholders, crmResolutionReasons, onDismiss }: IUnfilledPlaceholdersMessageBarProps) => {
  const languageStrings = React.useContext(LanguageContext);

  // Deduplicated defensively: the lists key the <li>s, and a server that ever reports the
  // same label twice must not produce duplicate React keys (or a doubled bullet).
  const uniquePlaceholders = React.useMemo(() => Array.from(new Set(placeholders ?? [])), [placeholders]);
  const uniqueUnusedValues = React.useMemo(() => Array.from(new Set(unusedValues ?? [])), [unusedValues]);
  const uniqueLeftoverTokens = React.useMemo(() => Array.from(new Set(columnPlaceholders ?? [])), [columnPlaceholders]);
  const uniqueReasons = React.useMemo(() => Array.from(new Set(crmResolutionReasons ?? [])), [crmResolutionReasons]);

  const crmTokens = React.useMemo(() => uniqueLeftoverTokens.filter(isCrmToken), [uniqueLeftoverTokens]);
  const columnTokens = React.useMemo(() => uniqueLeftoverTokens.filter(token => !isCrmToken(token)), [uniqueLeftoverTokens]);

  if (!uniquePlaceholders.length && !uniqueUnusedValues.length && !uniqueLeftoverTokens.length) {
    return null;
  }

  const listStyle: React.CSSProperties = { margin: "4px 0 0", paddingLeft: 20, maxHeight: 160, overflowY: "auto" };

  return (
    <MessageBar
      messageBarType={MessageBarType.warning}
      isMultiline={true}
      onDismiss={onDismiss}
      dismissButtonAriaLabel="Close"
      // The bar shares a fixed-height flex column with the grid (see the viewport in
      // ContentBodyFilesFolders); it must keep its natural height and let the grid shrink.
      styles={{ root: { flexShrink: 0 } }}
    >
      {uniquePlaceholders.length > 0 && <>
        <div>{languageStrings.CustomFieldsMissingWarning}</div>
        {/* Capped: a pathological template can report dozens of fields; the bar must never
            push the file list off-screen. */}
        <ul style={listStyle}>
          {uniquePlaceholders.map(placeholder => <li key={placeholder}>{placeholder}</li>)}
        </ul>
      </>}
      {uniqueUnusedValues.length > 0 && <>
        <div>{languageStrings.CustomFieldsUnusedWarning}</div>
        <ul style={listStyle}>
          {uniqueUnusedValues.map(value => <li key={value}>{value}</li>)}
        </ul>
      </>}
      {crmTokens.length > 0 && <>
        <div>{languageStrings.UnfilledCrmPlaceholdersWarning}</div>
        {/* The server's per-family failure reasons, verbatim (they are English sentences;
            precedent: ScanErrorsMessageBar shows server scan errors verbatim). Rendered
            only when CRM tokens exist - a reason with nothing to explain is noise. */}
        {uniqueReasons.map(reason => <div key={reason} style={{ fontStyle: "italic" }}>{reason}</div>)}
        <ul style={listStyle}>
          {crmTokens.map(token => <li key={token}>{token}</li>)}
        </ul>
      </>}
      {columnTokens.length > 0 && <>
        <div>{languageStrings.UnfilledColumnPlaceholdersWarning}</div>
        <ul style={listStyle}>
          {columnTokens.map(token => <li key={token}>{token}</li>)}
        </ul>
      </>}
    </MessageBar>
  );
});

UnfilledPlaceholdersMessageBar.displayName = "UnfilledPlaceholdersMessageBar";
